import { useState, useMemo } from 'react';
import { clsx } from 'clsx';
import { FilterPopoverShell } from './FilterPopoverShell';

/**
 * Excel-style column filter popover.
 *
 * - Sort A→Z / Z→A buttons at the top
 * - Search box that filters the value checklist
 * - (Select all) toggle
 * - Scrollable list of unique values with counts
 * - Clear / Apply buttons at the bottom
 *
 * Controlled component: parent owns the `selected` Set + `sortDir` state.
 * Positioning / outside-click / Escape handling lives in FilterPopoverShell.
 */
export function ColumnFilterPopover({
  columnLabel,
  values, // array of { value, count }
  selected, // Set of currently-selected values; empty Set or null = no filter
  onApply, // (Set | null) => void
  sortDir, // 'asc' | 'desc' | null
  onSort, // (dir) => void
  onClose,
}) {
  const [search, setSearch] = useState('');
  const [working, setWorking] = useState(() => (selected && selected.size > 0 ? new Set(selected) : null));

  const filtered = useMemo(() => {
    if (!search) return values;
    const q = search.toLowerCase();
    return values.filter((v) => String(v.value).toLowerCase().includes(q));
  }, [values, search]);

  const allSelected = working == null || working.size === values.length;
  const someSelected = working != null && working.size > 0 && working.size < values.length;

  const toggleAll = () => {
    if (allSelected) {
      setWorking(new Set()); // none
    } else {
      setWorking(null); // all (sentinel: null means "no filter")
    }
  };

  const toggle = (val) => {
    setWorking((cur) => {
      const next = cur ? new Set(cur) : new Set(values.map((v) => v.value));
      if (next.has(val)) next.delete(val);
      else next.add(val);
      return next;
    });
  };

  const apply = () => {
    if (working == null) {
      onApply?.(null);
    } else if (working.size === 0 || working.size === values.length) {
      onApply?.(null);
    } else {
      onApply?.(working);
    }
    onClose?.();
  };

  const clear = () => {
    onApply?.(null);
    onClose?.();
  };

  return (
    <FilterPopoverShell
      width={320}
      sortAscLabel="Sort A → Z"
      sortDescLabel="Sort Z → A"
      sortDir={sortDir}
      onSort={onSort}
      onClose={onClose}
      onClear={clear}
      onApply={apply}
    >
      <div className="p-2 border-b border-black/5">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${columnLabel}…`}
          className="w-full bg-surface-3 border border-transparent rounded px-3 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-gat-green/30"
        />
      </div>
      <button
        type="button"
        onClick={toggleAll}
        className="w-full px-3 py-2 text-[12px] font-semibold flex items-center gap-2 border-b border-black/5 hover:bg-surface-2"
      >
        <span
          className={clsx(
            'w-3.5 h-3.5 inline-flex items-center justify-center border rounded-[3px]',
            allSelected ? 'bg-gat-green border-gat-green' : someSelected ? 'bg-surface border-gat-green' : 'bg-surface border-black/25'
          )}
        >
          {allSelected && <span className="text-white text-[10px] leading-none">✓</span>}
          {someSelected && <span className="w-2 h-[2px] bg-gat-green rounded-[1px]" />}
        </span>
        (Select all · {values.length})
      </button>
      <div className="max-h-[220px] overflow-y-auto py-1">
        {filtered.length === 0 && <div className="px-3 py-3 text-[11.5px] text-text-muted text-center">No matches</div>}
        {filtered.map(({ value, count }) => {
          const checked = working == null || working.has(value);
          return (
            <button
              key={String(value)}
              type="button"
              onClick={() => toggle(value)}
              className="w-full px-3 py-1 text-[12px] flex items-center gap-2 cursor-pointer hover:bg-surface-2 text-left"
            >
              <span
                className={clsx(
                  'w-3.5 h-3.5 inline-flex items-center justify-center border rounded-[3px] shrink-0',
                  checked ? 'bg-gat-green border-gat-green' : 'bg-surface border-black/25'
                )}
              >
                {checked && <span className="text-white text-[10px] leading-none">✓</span>}
              </span>
              <span className="flex-1 truncate text-text-dark">{String(value)}</span>
              <span className="text-[10.5px] font-mono text-text-muted">{count}</span>
            </button>
          );
        })}
      </div>
    </FilterPopoverShell>
  );
}
